import { describeMutationError } from "@api/mutationErrors";
import {
  emailClient,
  isEditableDraft,
  type EmailDraft,
  type EmailDraftRevision,
  type EmailMessage,
} from "@email/emailClient";
import {
  emailKeys,
  useApproveEmailDraft,
  useReviseEmailDraft,
} from "@email/emailQueries";
import {
  Button,
  FieldError,
  Input,
  PaperPanel,
  Textarea,
} from "@scribenote/brian-design-system";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

export const gmailSource = (providerId: string): string =>
  `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(providerId)}`;

const DeliveryCheck = ({ draftId }: { draftId: string }) => {
  const queryClient = useQueryClient();
  const check = useMutation({
    mutationFn: () => emailClient.reconcileDraft(draftId),
    meta: { handled: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: emailKeys.all }),
  });
  return (
    <>
      <Button size="sm" busy={check.isPending} onClick={() => check.mutate()}>
        Check delivery
      </Button>
      {check.isError && (
        <FieldError>Could not check Gmail. Do not resend.</FieldError>
      )}
    </>
  );
};

/** `Name <addr>` wrappers as pasted from a mail client are unwrapped, and only
 * `,` / `;` separate entries, so a display name with spaces stays whole. */
const splitAddresses = (value: string): string[] =>
  value
    .split(/[,;]+/)
    .map((entry) => /<([^>]+)>/.exec(entry)?.[1] ?? entry)
    .map((address) => address.trim())
    .filter(Boolean);

type DraftRevise = ReturnType<typeof useReviseEmailDraft>;

/**
 * Bound to the `revision` it opened on: a save against a draft Brian revised
 * underneath is refused, and a newer revision is announced, not swapped in.
 */
const EmailDraftEditor = ({
  draft,
  revise,
  locked,
  onSave,
  onCancel,
}: {
  draft: EmailDraft;
  revise: DraftRevise;
  locked: boolean;
  onSave: (body: EmailDraftRevision) => void;
  onCancel: () => void;
}) => {
  const [base, setBase] = useState(draft);
  const [to, setTo] = useState(draft.recipient);
  const [cc, setCc] = useState(draft.cc.join(", "));
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const stale = draft.revision !== base.revision;
  const reload = () => {
    setBase(draft);
    setTo(draft.recipient);
    setCc(draft.cc.join(", "));
    setSubject(draft.subject);
    setBody(draft.body);
  };
  const changed =
    to !== base.recipient ||
    cc !== base.cc.join(", ") ||
    subject !== base.subject ||
    body !== base.body;
  const valid = to.trim() && subject.trim() && body.trim();
  const save = () =>
    onSave({
      revision: base.revision,
      to: to.trim(),
      cc: splitAddresses(cc),
      subject: subject.trim(),
      body,
    });
  return (
    <div className="mt-3 flex flex-col gap-3" data-dd-privacy="hidden">
      {locked && (
        <FieldError>
          This draft was {draft.status === "discarded" ? "deleted" : "approved"}{" "}
          by someone else while you were editing. Your changes cannot be saved
          to it. Copy anything you need before closing.
        </FieldError>
      )}
      {stale && !locked && (
        <div className="flex flex-wrap items-center gap-2 rounded-paper border border-rule bg-recess px-3 py-2 text-sm text-ink">
          <span>
            Brian updated this draft while you were editing. Loading the latest
            version replaces what you typed here.
          </span>
          <Button size="sm" variant="outline" onClick={reload}>
            Load latest version
          </Button>
        </div>
      )}
      <Input
        label="To"
        type="email"
        value={to}
        onChange={(event) => setTo(event.target.value)}
      />
      <Input
        label="Cc"
        value={cc}
        onChange={(event) => setCc(event.target.value)}
        hint="Separate addresses with commas"
      />
      <Input
        label="Subject"
        value={subject}
        onChange={(event) => setSubject(event.target.value)}
      />
      <Textarea
        label="Message"
        value={body}
        onChange={(event) => setBody(event.target.value)}
        minRows={6}
        maxRows={24}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="accent"
          size="sm"
          onClick={save}
          busy={revise.isPending}
          disabled={!changed || !valid || stale || locked || revise.isPending}
        >
          Save changes
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={revise.isPending}
        >
          {locked ? "Close" : "Cancel"}
        </Button>
      </div>
      {revise.isError && (
        <FieldError>
          {describeMutationError(revise.error, "save the draft")} Your text is
          still here; load the latest version and reapply it if the draft
          changed.
        </FieldError>
      )}
    </div>
  );
};

export const EmailDraftDetails = ({
  draft,
  canApprove,
}: {
  draft: EmailDraft;
  canApprove: boolean;
}) => {
  const approve = useApproveEmailDraft();
  const [submitted, setSubmitted] = useState(false);
  const [editing, setEditing] = useState(false);
  const revise = useReviseEmailDraft();
  const cache = useQueryClient();
  const recover = useMutation({
    mutationFn: () => emailClient.getDraft(draft.uuid),
    meta: { handled: true },
    onSuccess: (current) => {
      void cache.invalidateQueries({ queryKey: emailKeys.all });
      if (isEditableDraft(current.status)) setSubmitted(false);
    },
  });
  const submit = () => {
    setSubmitted(true);
    approve.mutate(
      { uuid: draft.uuid, revision: draft.revision },
      { onError: () => recover.mutate() },
    );
  };
  const editable = isEditableDraft(draft.status);
  const closeEditor = () => {
    revise.reset();
    setEditing(false);
  };
  // Stays mounted if the status moves on underneath, so typed text isn't dropped.
  if (editing)
    return (
      <EmailDraftEditor
        key={draft.uuid}
        draft={draft}
        revise={revise}
        locked={!editable}
        onSave={(body) =>
          revise.mutate({ uuid: draft.uuid, body }, { onSuccess: closeEditor })
        }
        onCancel={closeEditor}
      />
    );
  return (
    <>
      <p className="mt-3 whitespace-pre-wrap text-sm text-ink-body">
        {draft.body}
      </p>
      {draft.deliveryError && <FieldError>{draft.deliveryError}</FieldError>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {editable && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            disabled={submitted || approve.isPending}
          >
            Edit
          </Button>
        )}
        {editable && canApprove && (
          <>
            <Button
              variant="accent"
              size="sm"
              onClick={submit}
              busy={approve.isPending}
              disabled={submitted || approve.isPending}
            >
              {submitted
                ? "Approval submitted"
                : draft.status === "failed"
                  ? "Approve & retry"
                  : "Approve & send"}
            </Button>
            {submitted && !approve.isPending && (
              <Button
                size="sm"
                busy={recover.isPending}
                onClick={() => recover.mutate()}
              >
                Refresh approval status
              </Button>
            )}
          </>
        )}
        {draft.status === "draft" && !canApprove && (
          <span className="text-sm text-annotation">
            Reconnect Google to approve and send this draft.
          </span>
        )}
        {["sending", "preparing"].includes(draft.status) && (
          <span className="text-sm text-annotation">
            Approved and sending. Do not submit again.
          </span>
        )}
        {draft.status === "uncertain" && (
          <>
            <span className="text-sm text-ink-stopped">
              Delivery is uncertain. Check Gmail; do not resend.
            </span>
            <DeliveryCheck draftId={draft.uuid} />
          </>
        )}
        {draft.providerId && (
          <a
            href={gmailSource(draft.providerId)}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-accent underline underline-offset-2"
          >
            Open source in Gmail
          </a>
        )}
      </div>
      {recover.isError && (
        <FieldError>
          Approval status is unavailable. Do not submit again until status can
          be checked.
        </FieldError>
      )}
      {approve.isError && (
        <FieldError className="mt-3">
          {describeMutationError(approve.error, "approve and send the draft")}{" "}
          Refresh status before taking another action.
        </FieldError>
      )}
    </>
  );
};

export const EmailAttachments = ({
  attachments,
}: {
  attachments: EmailMessage["attachments"];
}) => (
  <>
    {attachments.length > 0 && (
      <div className="mt-3 flex flex-col gap-2">
        {attachments.map((attachment, index) => (
          <PaperPanel key={index} className="bg-recess p-3">
            <p className="break-words text-sm font-medium text-ink">
              {attachment.filename}
            </p>
            <p className="mt-0.5 text-xs text-annotation">
              {attachment.contentType} · {attachment.parseStatus}
            </p>
            {attachment.text && (
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap font-body text-sm text-ink-body">
                {attachment.text}
              </pre>
            )}
          </PaperPanel>
        ))}
      </div>
    )}
  </>
);
